import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Form, Button, FormGroup, Label } from 'reactstrap';
import ReactSelect from 'react-select';
import { createOrUpdatePermissions } from '../../../actions/organization';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InnerLoader from '../../../Components/Common/InnerLoader';

const subjectOptions = [
  { label: 'Organization', value: 'organization' },
  { label: 'Brand', value: 'brand' },
  { label: 'Country', value: 'country' },
  { label: 'Store', value: 'store' },
  { label: 'User', value: 'user' },
  { label: 'Promotion', value: 'promotion' },
  { label: 'Role', value: 'role' },
  { label: 'User Permissions', value: 'permission' },
  { label: 'All', value: 'all' },
];

const actionOptions = [
  { label: 'Manage', value: 'manage' },
  { label: 'Delete', value: 'delete' },
  { label: 'List', value: 'list' },
  { label: 'View', value: 'view' },
  { label: 'Approve', value: 'approve' },
  { label: 'Reject', value: 'reject' },
  { label: 'Publish', value: 'publish' },
  { label: 'Unpublish', value: 'unpublish' },
  { label: 'Archive', value: 'archive' },
  { label: 'Restore', value: 'restore' },
  { label: 'Assign', value: 'assign' },
  { label: 'Invite', value: 'invite' },
  { label: 'Download', value: 'download' },
  { label: 'Upload', value: 'upload' },
  { label: 'Export', value: 'export' },
  { label: 'Import', value: 'import' },
  { label: 'Execute', value: 'execute' },
  { label: 'Transfer', value: 'transfer' },
  { label: 'Clone', value: 'clone' },
];

const validationSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  actions: Yup.array().min(1, 'At least one action is required'),
});

const UserPermissionForm = ({
  handleClose = () => {},
  updateHandler = () => {},
  isFromPopup = false,
  subject = 'default',
  permission = null,
}) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      subject: '',
      actions: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const promises = values.actions.map((action) => {
          const data = {
            input: {
              permissionId: values?.permissionId,
              subject: values.subject,
              name: action.value,
              action: action.value,
            },
          };
          return createOrUpdatePermissions(data);
        });

        const res = await Promise.all(promises);

        if (res.every((r) => r?.permissionId)) {
          alertSuccess('Updated successfully');
          if (isFromPopup) {
            setTimeout(() => {
              resetFormValues();
              updateHandler();
              handleClose();
            }, 1000);
          }
        } else {
          alertError('Failed, Please try again later');
        }

        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (e) {
        console.log(e);
        alertError('Failed, Please try again later');
      }
    },
  });

  const { handleSubmit, setFieldValue, values, errors, touched, resetForm } =
    formik;

  useEffect(() => {
    if (permission) {
      resetForm({
        values: {
          permissionId: permission?.permissionId,
          subject: permission?.subject || '',
          actions: [permission?.name]
            .map((actionName) =>
              actionOptions.find((a) => a.value === actionName)
            )
            .filter(Boolean),
        },
      });
    }
  }, [permission]);

  const resetFormValues = () => {
    resetForm({
      values: {
        subject: '',
        actions: [],
      },
    });
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Subject</Label>
          <ReactSelect
            options={subjectOptions}
            value={subjectOptions.find((opt) => opt.value === values.subject)}
            onChange={(selected) => {
              setFieldValue('subject', selected?.value || '');
            }}
            isClearable
          />
          {touched.subject && errors.subject && (
            <div className="text-danger mt-1">{errors.subject}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Actions</Label>
          <ReactSelect
            isMulti
            options={actionOptions}
            value={actionOptions.filter((opt) =>
              values.actions.some((a) => a.value === opt.value)
            )}
            onChange={(selected) => {
              setFieldValue('actions', selected || []);
            }}
          />
          {touched.actions && errors.actions && (
            <div className="text-danger mt-1">{errors.actions}</div>
          )}
        </FormGroup>

        <Button type="submit" color="primary">
          Submit
        </Button>
      </Form>

      {/* <ToastContainer /> */}
      {loading && <InnerLoader />}
    </>
  );
};

export default UserPermissionForm;
